import React from 'react';
import styled from 'styled-components';
import Hero from '../components/Hero';
import ServiceDetail from '../components/ServiceDetail';
import ContactForm from '../components/ContactForm';
import SectionTitle from '../components/SectionTitle';
import SEO from '../components/SEO';
import { lightingProductionImage, lightingProductionDetailImage } from '../assets/images';
import { 
  FaLightbulb, 
  FaMusic, 
  FaBuilding, 
  FaUsers 
} from 'react-icons/fa';

const LightingProduction = () => {
  const features = [
    'Event lighting design & production',
    'Intelligent & moving head fixtures',
    'LED uplighting & wash lighting',
    'Custom gobo & pattern projection',
    'Truss systems & rigging',
    'DMX programming & control',
    'Atmospheric effects (haze, fog)',
    'Concert & stage lighting'
  ];

  return (
    <main>
      <SEO 
        title="Lighting Production | TK DJ Services"
        description="Professional lighting design and production services for events, concerts, and venues. Custom lighting solutions to enhance your event experience."
        url="/lighting-production"
        canonical="/lighting-production"
        image="/images/AdobeStock_1056060992.jpeg"
      />
      <Hero 
        backgroundImage={lightingProductionImage}
        title="Lighting Production"
        subtitle="Professional lighting design and production services for events, concerts, and venues."
        buttonText="Explore Options"
        buttonLink="#services"
      />
      
      <ServiceDetailSection>
        <div className="container">
          <ServiceDetail 
            title="Immersive Visual Experiences & Design"
            description="TK DJ Services offers comprehensive lighting production solutions for events, venues, and performances. Our expertise in lighting design, installation, and programming creates immersive visual experiences that complement and enhance any atmosphere. From subtle ambient lighting to dynamic show productions, we deliver professional lighting solutions tailored to your specific needs."
            features={features}
            image={lightingProductionDetailImage}
            ctaText="Get a Quote"
            ctaLink="#contact"
          />
        </div>
      </ServiceDetailSection>
      
      <ServicesSection id="services">
        <div className="container">
          <SectionTitle 
            title="Our Lighting Services" 
            subtitle="We offer a range of lighting production services to transform your venue or event with stunning visual effects."
            center
          />
          
          <ServiceGrid>
            <ServiceCard>
              <ServiceIcon>
                <FaLightbulb />
              </ServiceIcon>
              <ServiceTitle>Event Lighting Design</ServiceTitle>
              <ServiceDescription>
                Professional lighting design and installation for weddings, corporate events, concerts, and private parties. We create custom lighting plans that enhance your event's atmosphere and highlight key elements, from subtle ambient lighting to dynamic effects that transform your space.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaBuilding />
              </ServiceIcon>
              <ServiceTitle>Venue Lighting Upgrades</ServiceTitle>
              <ServiceDescription>
                Comprehensive lighting upgrades for nightclubs, bars, restaurants, and event spaces. We design and install permanent lighting solutions that enhance your venue's atmosphere, improve energy efficiency, and create memorable experiences for your patrons.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaMusic />
              </ServiceIcon>
              <ServiceTitle>Intelligent Lighting Programming</ServiceTitle>
              <ServiceDescription>
                Expert programming of DMX-controlled and intelligent lighting systems. We create dynamic lighting shows synchronized with music and events, utilizing moving heads, LED fixtures, lasers, and other advanced lighting technology to create immersive experiences.
              </ServiceDescription>
            </ServiceCard>

          </ServiceGrid>
        </div>
      </ServicesSection>
      
      <ProcessSection>
        <div className="container">
          <SectionTitle 
            title="Our Lighting Process" 
            subtitle="We follow a comprehensive process to ensure your lighting production meets your vision and exceeds expectations."
            center
          />
          
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>01</StepNumber>
              <StepTitle>Consultation</StepTitle>
              <StepDescription>
                We begin with a detailed consultation to understand your vision, requirements, and budget. This helps us create a lighting concept that aligns perfectly with your goals.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>02</StepNumber>
              <StepTitle>Design</StepTitle>
              <StepDescription>
                Our lighting designers create a comprehensive lighting plan, including fixture selection, placement, and programming concepts tailored to your space and needs.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>03</StepNumber>
              <StepTitle>Installation</StepTitle>
              <StepDescription>
                Our experienced technicians handle the professional installation of all lighting equipment, ensuring safe and proper setup for optimal performance.
              </StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>04</StepNumber>
              <StepTitle>Programming</StepTitle>
              <StepDescription>
                We program your lighting system to create the desired effects, scenes, and shows, synchronizing with music or events as needed for a cohesive experience.
              </StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </div>
      </ProcessSection>
      
      <ContactSection id="contact">
        <div className="container">
          <SectionTitle 
            title="Transform Your Space" 
            subtitle="Ready to elevate your venue or event with professional lighting production? Contact us today to discuss your vision."
            center
          />
          <ContactForm />
        </div>
      </ContactSection>
    </main>
  );
};

const ServiceDetailSection = styled.section`
  padding: 8rem 0 5rem;
`;

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
`;

const ServiceTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  padding: 5rem 0;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ProcessStep = styled.div`
  text-align: center;
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const StepNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-secondary);
  opacity: 0.5;
  margin-bottom: 1rem;
`;

const StepTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

export default LightingProduction; 